import React from 'react'
import { Form, Col, Spinner } from 'react-bootstrap'
import constants from '../../../config/constants'
import { toPersonName, isValidPersonName } from '../../../utility/stringUtils'
import { Formik } from 'formik'
import * as yup from 'yup'

interface SignupFormProps {
  emailSignupCallback: (
    company: string,
    firstName: string,
    lastName: string,
    email: string,
    password: string
  ) => void
  isLoading: boolean
}

const msg = constants.inlineError
const schema = yup.object({
  company: yup
    .string()
    .min(constants.companyNameMinLength, msg.auth.invalidCompanyName)
    .required(msg.fieldRequired.replace('%s', 'Company')),
  firstName: yup
    .string()
    .required(msg.fieldRequired.replace('%s', 'First name'))
    .test('ValidPersonName', msg.auth.invalidFirstName, value => {
      return isValidPersonName(value)
    }),
  lastName: yup
    .string()
    .required(msg.fieldRequired.replace('%s', 'Last name'))
    .test('ValidPersonName', msg.auth.invalidLastName, value => {
      return isValidPersonName(value)
    }),
  email: yup
    .string()
    .email(msg.auth.invalidEmail)
    .required(msg.fieldRequired.replace('%s', 'Email')),
  password: yup
    .string()
    .min(constants.passwordMinLength, msg.auth.invalidPassword)
    .required(msg.fieldRequired.replace('%s', 'Password'))
})

const SignupForm: React.FC<SignupFormProps> = ({
  emailSignupCallback,
  isLoading
}) => {
  return (
    <Formik
      validationSchema={schema}
      onSubmit={values => {
        emailSignupCallback(
          values.company,
          values.firstName,
          values.lastName,
          values.email,
          values.password
        )
      }}
      initialValues={{
        company: '',
        firstName: '',
        lastName: '',
        email: '',
        password: ''
      }}
    >
      {({
        handleSubmit,
        handleChange,
        handleBlur,
        values,
        errors,
        touched,
        setFieldValue
      }) => (
        <Form noValidate onSubmit={handleSubmit}>
          <Form.Group>
            <Form.Label className="mochi-sign-in-label" htmlFor="login-company">
              Company
            </Form.Label>
            <Form.Control
              id="login-company"
              type="text"
              name="company"
              placeholder="Enter company name"
              value={values.company}
              onChange={handleChange}
              onBlur={handleBlur}
              isInvalid={touched.company && !!errors.company}
              isValid={touched.company && !errors.company}
            />
            <Form.Control.Feedback type="invalid">
              {errors.company}
            </Form.Control.Feedback>
          </Form.Group>
          <Form.Row>
            <Form.Group as={Col}>
              <Form.Label
                className="mochi-sign-in-label"
                htmlFor="login-first-name"
              >
                First Name
              </Form.Label>
              <Form.Control
                id="login-first-name"
                type="text"
                name="firstName"
                placeholder="Enter first name"
                value={values.firstName}
                onBlur={handleBlur}
                isInvalid={touched.firstName && !!errors.firstName}
                isValid={touched.firstName && !errors.firstName}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                  const fName = toPersonName(e.target.value)
                  setFieldValue('firstName', fName)
                }}
              />
              <Form.Control.Feedback type="invalid">
                {errors.firstName}
              </Form.Control.Feedback>
            </Form.Group>
            <Form.Group as={Col}>
              <Form.Label
                className="mochi-sign-in-label"
                htmlFor="login-last-name"
              >
                Last Name
              </Form.Label>
              <Form.Control
                id="login-last-name"
                type="text"
                name="lastName"
                placeholder="Enter last name"
                value={values.lastName}
                onBlur={handleBlur}
                isInvalid={touched.lastName && !!errors.lastName}
                isValid={touched.lastName && !errors.lastName}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                  const lName = toPersonName(e.target.value)
                  setFieldValue('lastName', lName)
                }}
              />
              <Form.Control.Feedback type="invalid">
                {errors.lastName}
              </Form.Control.Feedback>
            </Form.Group>
          </Form.Row>
          <Form.Group>
            <Form.Label className="mochi-sign-in-label" htmlFor="login-email">
              Email
            </Form.Label>
            <Form.Control
              id="login-email"
              type="email"
              name="email"
              placeholder="Enter email"
              value={values.email}
              onChange={handleChange}
              onBlur={handleBlur}
              isInvalid={touched.email && !!errors.email}
              isValid={touched.email && !errors.email}
            />
            <Form.Control.Feedback type="invalid">
              {errors.email}
            </Form.Control.Feedback>
          </Form.Group>
          <Form.Group>
            <Form.Label
              className="mochi-sign-in-label"
              htmlFor="login-password"
            >
              Password
            </Form.Label>
            <Form.Control
              id="login-password"
              type="password"
              name="password"
              placeholder="Enter password"
              value={values.password}
              onChange={handleChange}
              onBlur={handleBlur}
              isInvalid={touched.password && !!errors.password}
              isValid={touched.password && !errors.password}
            />
            <Form.Control.Feedback type="invalid">
              {errors.password}
            </Form.Control.Feedback>
          </Form.Group>
          <div className="text-center mt-4">
            <button
              className="mochi-button orange"
              type="submit"
              data-testid="signup-submit-button"
              disabled={isLoading}
            >
              {isLoading && (
                <span className="mr-2">
                  <Spinner animation="border" size="sm" />
                </span>
              )}
              {isLoading ? 'Signing up...' : 'Sign up'}
            </button>
          </div>
        </Form>
      )}
    </Formik>
  )
}

export default SignupForm
